import type { ImgHTMLAttributes } from 'react';
import { useMemo, useState } from 'react';

import { Cloud } from '@remarkable/ark-icons';

import { useIntegrationProviders } from 'src/api/queries';

export function IntegrationIcon({
  iconId,
  provider,
  alt = 'Integration icon',
  ...props
}: ImgHTMLAttributes<HTMLImageElement> & {
  iconId?: string;
  provider?: string;
  ['data-cy']?: string;
}) {
  const integrationProviders = useIntegrationProviders();

  const providerData = useMemo(() => {
    if (!integrationProviders.data) return null;
    return integrationProviders.data.find((p) => p.typeName === provider);
  }, [integrationProviders.data, provider]);

  const [showFallbackIcon, setShowFallbackIcon] = useState(false);

  if (showFallbackIcon) {
    return (
      <Cloud
        size={24}
        id={props.id}
        className={props.className}
        style={props.style}
        alt={alt}
        aria-label={props['aria-label']}
        aria-description={props['aria-description']}
        data-cy={props['data-cy']}
      />
    );
  }

  if (iconId) {
    return (
      <img
        src={`/${iconId}.svg`}
        alt={alt}
        {...props}
        onError={() => setShowFallbackIcon(true)}
      />
    );
  }

  if (integrationProviders.isLoading || !providerData) {
    return (
      <Cloud
        size={24}
        id={props.id}
        className={props.className}
        style={props.style}
        alt={alt}
        aria-label={props['aria-label']}
        aria-description={props['aria-description']}
        data-cy={props['data-cy']}
      />
    );
  }

  return (
    <img
      src={`/${providerData.iconId}.svg`}
      alt={alt}
      {...props}
      onError={() => setShowFallbackIcon(true)}
    />
  );
}
